<template>
  <div class="panel-view" :class="{ 'panel-view--enter': fromOnboarding }">
    <TopBar
      :park="park"
      :period="kpis?.period"
      :loading="loading"
      :alerts="alerts"
      @alert-action="onAlertAction"
      @alert-dismiss="onAlertDismiss"
      @view-all-alerts="showAllAlerts = true"
    />
    <div v-if="loading" class="panel-loading">
      <span class="mdi mdi-loading panel-loading__spinner"></span>
      <span class="panel-loading__text">Cargando panel...</span>
    </div>

    <template v-else>
      <KpiRow :kpis="kpis" />
      <main class="panel-content">
          <div class="panel-grid">
          <section class="panel-grid__center">
            <ParkMap
              v-if="park && park.mapBackgroundSvg"
              :park="park"
              :active-filter="mapStatusFilter"
              @update:active-filter="mapStatusFilter = $event"
              @select-plot="onSelectPlot"
            />
            <ParkPlotsGrid
              v-else-if="park && park.plots?.length"
              :plots="park.plots"
              @select-plot="onSelectPlot"
              @add-plot="openPlotModal('create')"
              @import-plots="openPlotModal('import')"
            />
            <ParkEmptyState
              v-else-if="park"
              @register-plot="openPlotModal('create')"
              @import-plots="openPlotModal('import')"
            />
          </section>
          <aside class="panel-grid__side">
            <SidePanel
              :selected-plot="selectedPlot"
              :plot-detail="plotDetail"
              :plot-loading="plotLoading"
              :plots="park?.plots || []"
              :active-filter="mapStatusFilter"
              :occupancy-history="occupancyHistory"
              :show-all-alerts="showAllAlerts"
              :alerts="alerts"
              @close-plot="clearSelectedPlot"
              @update:active-filter="mapStatusFilter = $event"
              @close-all-alerts="showAllAlerts = false"
              @alert-action="onAlertAction"
              @alert-dismiss="onAlertDismiss"
              @open-ticket="onOpenTicketFromPlot"
              @plot-status-changed="onPlotStatusChanged"
              @plot-deleted="onPlotDeleted"
            />
          </aside>
        </div>
      </main>
    </template>

    <PlotQuickAddModal
      v-if="plotModalMode && park"
      :park-id="park.id"
      :mode="plotModalMode"
      @close="plotModalMode = null"
      @created="onPlotsCreated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import TopBar from '@/components/Panel/TopBar.vue'
import KpiRow from '@/components/Panel/KpiRow.vue'
import ParkMap from '@/components/Panel/ParkMap.vue'
import ParkPlotsGrid from '@/components/Panel/ParkPlotsGrid.vue'
import ParkEmptyState from '@/components/Panel/ParkEmptyState.vue'
import PlotQuickAddModal from '@/components/Panel/PlotQuickAddModal.vue'
import SidePanel from '@/components/Panel/SidePanel.vue'
import { getParks, getParkById, getParkKpis, getOccupancyHistory } from '@/services/parks'
import { getPlotDetail } from '@/services/plots'
import { getParkAlerts, dismissAlert as dismissAlertApi } from '@/services/alerts'
import { ALERT_ROUTES } from '@/services/alertRoutes'

const router = useRouter()
// Al llegar desde el onboarding, el panel entra con un fade para suavizar la transición.
let fromOnboarding = false
try {
  fromOnboarding = sessionStorage.getItem('parkosFromOnboarding') === '1'
  if (fromOnboarding) sessionStorage.removeItem('parkosFromOnboarding')
} catch {
  fromOnboarding = false
}
const alerts = ref([])
const showAllAlerts = ref(useRoute().query.alertas === '1')

async function onAlertAction(alert) {
  removeAlertAndDismiss(alert)
  const buildRoute = ALERT_ROUTES[alert.actionType]
  if (buildRoute) router.push(buildRoute(alert.actionPayload || {}))
}

async function onAlertDismiss(alert) {
  removeAlertAndDismiss(alert)
}

async function removeAlertAndDismiss(alert) {
  alerts.value = alerts.value.filter((a) => a.key !== alert.key)
  try {
    await dismissAlertApi(park.value.id, alert.key)
  } catch (e) {
    alerts.value.push(alert) // si falla el guardado, la regresamos para no perderla silenciosamente
  }
}

// Un ticket de mantenimiento señalado en el mapa/grid o en el panel lateral de
// una nave reutiliza la misma ruta que ya usan las alertas de "ver ticket".
function onOpenTicketFromPlot(ticketId) {
  router.push(ALERT_ROUTES['view-ticket']({ ticketId }))
}

const park = ref(null)
const kpis = ref(null)
const loading = ref(true)

const occupancyHistory = ref([])
const mapStatusFilter = ref(null)

const selectedPlot = ref(null)
const plotDetail = ref(null)
const plotLoading = ref(false)

async function onSelectPlot(plot) {
  selectedPlot.value = plot
  plotLoading.value = true
  try {
    plotDetail.value = await getPlotDetail(plot.id)
  } finally {
    plotLoading.value = false
  }
}

async function onPlotStatusChanged(updated) {
  plotDetail.value = updated
  if (park.value) park.value = await getParkById(park.value.id) // el mapa y la lista toman el color nuevo
}

async function onPlotDeleted() {
  clearSelectedPlot()
  if (park.value) park.value = await getParkById(park.value.id)
}

function clearSelectedPlot() {
  selectedPlot.value = null
  plotDetail.value = null
}

const plotModalMode = ref(null) // null | 'create' | 'import'

function openPlotModal(mode) {
  plotModalMode.value = mode
}

async function onPlotsCreated() {
  plotModalMode.value = null
  if (!park.value) return
  park.value = await getParkById(park.value.id) // refrescamos para que el mapa/grid vea las naves nuevas
}

onMounted(async () => {
  try {
    const parks = await getParks()
    if (parks[0]) {
        const [fullPark, kpiData, history, alertsData] = await Promise.all([
        getParkById(parks[0].id),
        getParkKpis(parks[0].id),
        getOccupancyHistory(parks[0].id),
        getParkAlerts(parks[0].id),
      ])
      park.value = fullPark
      kpis.value = kpiData
      occupancyHistory.value = history
      alerts.value = alertsData
    }
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.panel-view--enter { animation: panelEnter 0.6s ease-out both; }
@keyframes panelEnter { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .panel-view--enter { animation: none; } }

.panel-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.panel-content {
  flex: 1;
  min-height: 0;
  padding: 1.5rem 2rem 2rem;
  display: flex;
  flex-direction: column;
}

.panel-grid {
  display: grid;
  grid-template-columns: 7fr 3fr;
  grid-template-rows: 1fr;
  gap: 1rem;
  flex: 1;
  min-height: 0;
}

.panel-grid__center {
  min-width: 0;
  min-height: 0;
}

.panel-grid__side {
  min-width: 0;
  min-height: 0;
}

.panel-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-secondary);
}

.panel-loading__spinner {
  font-size: 1.8rem;
  animation: panel-spin 0.8s linear infinite;
}

.panel-loading__text {
  font-size: 0.85rem;
  font-family: var(--font-body);
}

@keyframes panel-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>